import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Spin, Steps, Typography, message } from 'antd';
import {
  CarOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  PictureOutlined,
  TeamOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import eventService from '../services/eventService';
import { getUserId, getUserRole } from '../utils/tokenUtils';
import {
  EventAssetsStep,
  EventBasicsStep,
  EventDetailsAndCustomStep,
  EventDriverStep,
  EventLocationStep,
  EventMediaStep,
  EventSummaryStep
} from '../components/eventWizard/EventWizardSteps';
import {
  getCustomFieldName,
  getFieldRules,
  serializeCustomFieldValue
} from '../utils/eventWizardUtils';
import '../assets/styles/eventWizard.css';

const { Step } = Steps;
const { Title } = Typography;

const INITIAL_FORM_DATA = {
  oemName: 'No OEM',
  eventTypeId: 1,
  involvementId: 1,
  eventLocationId: 1,
  plannedDate: null,
  selectedCompanyId: undefined,
  selectedCompany: null,
  controllerName: '',
  controllerPhone: undefined,
  selectedDriverId: undefined,
  selectedDriver: null,
  driverName: '',
  driverPhone: undefined,
  horse: null,
  trailer1: null,
  trailer2: null,
  horseFault: false,
  trailer1Fault: false,
  trailer2Fault: false,
  address: '',
  latitude: '',
  longitude: '',
  isTelematicsLocation: 0,
  fmVehicleId: 0,
  description: '',
  internalNotes: '',
  otherEventInfo: '',
  orderNumber: '',
  imageryFiles: []
};

const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || '');
    resolve(result.includes(',') ? result.slice(result.indexOf(',') + 1) : result);
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const convertWebpToJpeg = async (file) => {
  const source = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const image = new Image();
  image.src = source;
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
  });
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Image conversion is unavailable');
  context.drawImage(image, 0, 0);
  return canvas.toDataURL('image/jpeg', 0.92).split(',')[1];
};

const buildMediaPayload = async (fileList) => Promise.all(
  (fileList || [])
    .map((item) => item.originFileObj)
    .filter(Boolean)
    .map(async (file) => {
      const isWebp = file.type === 'image/webp' || String(file.name || '').toLowerCase().endsWith('.webp');
      return {
        Imagery: isWebp ? await convertWebpToJpeg(file) : await readFileAsBase64(file),
        MediaType: isWebp ? 'image/jpeg' : (file.type || 'application/octet-stream')
      };
    })
);

const formatApiError = (error) => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (error?.code === 'ECONNABORTED' || error?.code === 'ERR_CANCELED') {
    return 'The event request was interrupted and may still have completed. Check Events before trying again.';
  }
  return data?.message || error?.message || 'Failed to create event';
};

const normalizeDefaultValue = (field, value) => {
  if (value === undefined || value === null) {
    if (field.fieldType === 'Toggle') return false;
    if (field.fieldType === 'Checkbox') return [];
    return undefined;
  }
  if (field.fieldType === 'DatePicker') return dayjs(value);
  if (field.fieldType === 'TimePicker') return dayjs(value, 'HH:mm');
  return value;
};

const EventWizard = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [lookups, setLookups] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [oems, setOems] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loadingInitialData, setLoadingInitialData] = useState(true);
  const [loadingCustomFields, setLoadingCustomFields] = useState(false);
  const [telematicsLoading, setTelematicsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAssetError, setShowAssetError] = useState(false);
  const submittingRef = useRef(false);
  const previousCustomNamesRef = useRef([]);
  const assetErrorRef = useRef(null);
  const userRole = String(getUserRole() || '').toUpperCase();

  const updateFormData = useCallback((patch) => {
    setFormData((previous) => ({ ...previous, ...patch }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingInitialData(true);
    Promise.all([
      eventService.getLookups(),
      eventService.getCompanies(),
      userRole === 'CONSULTANT' ? eventService.getOems() : Promise.resolve([]),
      eventService.getFinanceCurrencies().catch(() => [])
    ])
      .then(([lookupRows, companyRows, oemRows, currencyRows]) => {
        if (cancelled) return;
        setLookups(lookupRows);
        setCompanies(companyRows);
        setOems(oemRows);
        setCurrencies(currencyRows);

        const initialType = (lookupRows?.eventTypes || []).find((item) => Number(item.id) === 1)
          || lookupRows?.eventTypes?.[0];
        const initialLocation = (lookupRows?.eventLocations || []).find((item) => Number(item.id) === 1)
          || lookupRows?.eventLocations?.[0];
        const initialValues = {
          ...INITIAL_FORM_DATA,
          eventTypeId: initialType ? Number(initialType.id) : undefined,
          eventLocationId: initialLocation ? Number(initialLocation.id) : undefined
        };
        setFormData(initialValues);
        form.setFieldsValue(initialValues);
      })
      .catch((error) => {
        console.error('Failed to load event creation data:', error);
        message.error('Failed to load event creation data');
      })
      .finally(() => {
        if (!cancelled) setLoadingInitialData(false);
      });

    return () => {
      cancelled = true;
    };
  }, [form, userRole]);

  useEffect(() => {
    let cancelled = false;
    if (!formData.eventTypeId) {
      setCustomFields([]);
      return undefined;
    }

    setLoadingCustomFields(true);
    eventService.getCustomFields(formData.eventTypeId)
      .then((rows) => {
        if (cancelled) return;
        const sortedRows = [...(rows || [])].sort((left, right) => left.fieldOrder - right.fieldOrder);
        const nextNames = sortedRows.map(getCustomFieldName);
        const resetValues = Object.fromEntries(
          previousCustomNamesRef.current
            .filter((name) => !nextNames.includes(name))
            .map((name) => [name, undefined])
        );
        sortedRows.forEach((field) => {
          const name = getCustomFieldName(field);
          if (form.getFieldValue(name) !== undefined) return;
          const rules = getFieldRules(field);
          resetValues[name] = normalizeDefaultValue(field, rules.default);
        });
        previousCustomNamesRef.current = nextNames;
        setCustomFields(sortedRows);
        form.setFieldsValue(resetValues);
        updateFormData(resetValues);
      })
      .catch((error) => {
        console.error('Failed to load event custom fields:', error);
        if (!cancelled) {
          setCustomFields([]);
          message.error('Failed to load custom fields');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCustomFields(false);
      });

    return () => {
      cancelled = true;
    };
  }, [form, formData.eventTypeId, updateFormData]);

  const currencyOptions = useMemo(
    () => currencies.map((currency) => ({
      value: currency.currencyCode,
      label: `${currency.currencyCode} - ${currency.currencyName} (${currency.currencySymbol})`
    })),
    [currencies]
  );

  const handleAssetSelect = async (kind, vehicle) => {
    const faultKey = kind === 'horse' ? 'horseFault' : kind === 'trailer1' ? 'trailer1Fault' : 'trailer2Fault';
    const selectedAsset = { ...vehicle, mileage: null };
    updateFormData({ [kind]: selectedAsset, [faultKey]: true });
    setShowAssetError(false);

    if (kind !== 'horse') return;
    setTelematicsLoading(true);
    try {
      const location = await eventService.getTelematicsLocation(vehicle);
      const latitude = Number(location?.latitude);
      const longitude = Number(location?.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        throw new Error('No telematics coordinates returned');
      }

      const mileage = Number.parseFloat(String(location?.mileage || '').replace(',', '.'));
      const patch = {
        horse: {
          ...selectedAsset,
          mileage: Number.isFinite(mileage) ? Math.round(mileage) : null
        },
        address: location?.address || '',
        latitude,
        longitude,
        isTelematicsLocation: 1,
        fmVehicleId: location?.fmVehicleId || 0
      };
      updateFormData(patch);
      form.setFieldsValue(patch);
      message.success('Vehicle location loaded from telematics');
    } catch (error) {
      console.warn('Vehicle telematics unavailable:', error);
      updateFormData({ isTelematicsLocation: 0, fmVehicleId: 0 });
      message.info('Telematics location is unavailable. Capture the location manually.');
    } finally {
      setTelematicsLoading(false);
    }
  };

  const validateCurrentStep = async () => {
    if (currentStep === 0) {
      const selectedType = (lookups?.eventTypes || []).find(
        (item) => Number(item.id) === Number(formData.eventTypeId)
      );
      const names = [
        'eventTypeId',
        'involvementId',
        'eventLocationId',
        'selectedCompanyId',
        'controllerName',
        'controllerPhone'
      ];
      if (userRole === 'CONSULTANT') names.push('oemName');
      if (selectedType?.isPlanned) names.push('plannedDate');
      await form.validateFields(names);
      return;
    }
    if (currentStep === 1) {
      await form.validateFields(['selectedDriverId', 'driverName', 'driverPhone']);
      return;
    }
    if (currentStep === 2) {
      if (!formData.horse && !formData.trailer1 && !formData.trailer2) {
        setShowAssetError(true);
        window.requestAnimationFrame(() => {
          assetErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        throw new Error('Asset required');
      }
      setShowAssetError(false);
      return;
    }
    if (currentStep === 3) {
      await form.validateFields(['address', 'latitude', 'longitude']);
      return;
    }
    if (currentStep === 4) {
      await form.validateFields([
        'description',
        ...customFields.map(getCustomFieldName)
      ]);
    }
  };

  const nextStep = async () => {
    try {
      await validateCurrentStep();
      setCurrentStep((step) => Math.min(step + 1, 6));
    } catch (error) {
      if (error?.errorFields?.length) {
        message.error('Please complete the required fields');
        window.requestAnimationFrame(() => {
          form.scrollToField(error.errorFields[0].name, {
            behavior: 'smooth',
            block: 'center'
          });
        });
      }
    }
  };

  const previousStep = () => setCurrentStep((step) => Math.max(step - 1, 0));

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    try {
      const values = { ...form.getFieldsValue(true), ...formData };
      if (!values.horse && !values.trailer1 && !values.trailer2) {
        throw new Error('Select at least one horse or trailer');
      }

      const imagery = await buildMediaPayload(values.imageryFiles);
      const driverPhone = values.driverPhone?.nationalNumber
        ? `${values.driverPhone.dialCode}${values.driverPhone.nationalNumber}`
        : '';
      const controllerPhone = values.controllerPhone?.nationalNumber
        ? `${values.controllerPhone.dialCode}${values.controllerPhone.nationalNumber}`
        : '';
      const horse = values.horse || {};
      const trailer1 = values.trailer1 || {};
      const trailer2 = values.trailer2 || {};
      const userId = getUserId();

      const payload = {
        breakdownRequest: {
          ControllerName: values.controllerName,
          ControllerContactNo: controllerPhone,
          CompanyName: values.selectedCompany?.name || '',
          CompanyId: String(values.selectedCompany?.id || values.selectedCompanyId || ''),
          SelectedCompany: values.selectedCompany?.name || '',
          SelectedDriverId: String(values.selectedDriverId || ''),
          UserId: userId,
          UserModelId: userId,
          DriverName: values.driverName,
          DriverCell: driverPhone,
          MobileNumber: driverPhone,
          Address: values.address,
          AddressFromLocation: values.address,
          BreakdownAddress: values.address,
          Latitude: Number(values.latitude),
          Longitude: Number(values.longitude),
          FaultOnHorse: values.horseFault ? 1 : 0,
          FaultOnTrailer: values.trailer1Fault ? 1 : 0,
          FaultOnSecondTrailer: values.trailer2Fault ? 1 : 0,
          VehicleId: horse.id || '',
          TruckRegistrationNumber: horse.registration || '',
          TruckVINNumber: String(horse.vin || '').toUpperCase(),
          TruckFleet: horse.fleet || '',
          TruckMake: horse.make || '',
          TruckModel: horse.model || '',
          TruckYear: horse.year ? String(horse.year) : '',
          TrailerVehicleId: trailer1.id || '',
          TrailerRegistrationNumber: trailer1.registration || '',
          TrailerVINNumber: String(trailer1.vin || '').toUpperCase(),
          TrailerFleet: trailer1.fleet || '',
          TrailerMake: trailer1.make || '',
          TrailerModel: trailer1.model || '',
          TrailerYear: trailer1.year ? String(trailer1.year) : '',
          SecondTrailerVehicleId: trailer2.id || '',
          SecondTrailerRegistrationNumber: trailer2.registration || '',
          SecondTrailerVINNumber: String(trailer2.vin || '').toUpperCase(),
          SecondTrailerFleet: trailer2.fleet || '',
          SecondTrailerMake: trailer2.make || '',
          SecondTrailerModel: trailer2.model || '',
          SecondTrailerYear: trailer2.year ? String(trailer2.year) : '',
          LeaseType: horse.leaseType || '',
          Mileage: horse.mileage != null ? String(horse.mileage) : '',
          CostCentreName: horse.costCentreName || '',
          OrderNumber: values.orderNumber || '',
          BreakdownDescription: values.description,
          Description: values.description,
          BreakdownType: String(values.eventTypeId),
          OtherBreakdownInfo: values.otherEventInfo || '',
          InternalCaseNotes: values.internalNotes || '',
          Imagery: imagery,
          BreakdownRequestReference: values.oemName === 'No OEM' ? '' : (values.oemName || ''),
          Warranty: '',
          AttendingMechanic: '',
          IsVehicleLoadedTextVal: '',
          BreakdownClassType: '',
          EquipmentFleetNumber: '',
          EquipmentMake: '',
          EquipmentModel: '',
          EquipmentSerialNumber: '',
          EquipmentYear: '',
          IsTelematicsLocation: Number(values.isTelematicsLocation || 0),
          FmVehicleId: Number(values.fmVehicleId || 0),
          OriginatorReference: 'PWA'
        },
        newFields: {
          typeId: Number(values.eventTypeId),
          involvementId: Number(values.involvementId),
          locationId: Number(values.eventLocationId),
          plannedDate: values.plannedDate && dayjs.isDayjs(values.plannedDate)
            ? values.plannedDate.toISOString()
            : null
        },
        customFields: customFields.map((field) => ({
          id: Number(field.id),
          fieldName: field.fieldName,
          fieldType: field.fieldType,
          value: serializeCustomFieldValue(field, values[getCustomFieldName(field)])
        }))
      };

      const response = await eventService.createEvent(payload);
      const breakdownId = response?.breakdownId || response?.BreakdownId;
      if (!breakdownId) throw new Error(response?.message || response?.Message || 'Event was created without an event ID');

      message.success(response?.message || response?.Message || 'Event created successfully');
      navigate(`/events/${breakdownId}`, {
        state: {
          selectedId: breakdownId,
          selectedRef: response?.breakdownReference || response?.BreakdownReference,
          from: '/events'
        }
      });
    } catch (error) {
      console.error('Failed to create event:', error);
      message.error(formatApiError(error));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const stepItems = [
    { icon: <FileTextOutlined style={{ fontSize: '16px' }} /> },
    { icon: <TeamOutlined style={{ fontSize: '16px' }} /> },
    { icon: <CarOutlined style={{ fontSize: '16px' }} /> },
    { icon: <EnvironmentOutlined style={{ fontSize: '16px' }} /> },
    { icon: <UserOutlined style={{ fontSize: '16px' }} /> },
    { icon: <PictureOutlined style={{ fontSize: '16px' }} /> },
    { icon: <CheckCircleOutlined style={{ fontSize: '16px' }} /> }
  ];

  const renderStep = () => {
    const commonProps = { form, formData, updateFormData };
    switch (currentStep) {
      case 0:
        return (
          <EventBasicsStep
            {...commonProps}
            lookups={lookups}
            companies={companies}
            oems={oems}
            loading={loadingInitialData}
            userRole={userRole}
          />
        );
      case 1:
        return <EventDriverStep {...commonProps} />;
      case 2:
        return (
          <EventAssetsStep
            formData={formData}
            updateFormData={updateFormData}
            onAssetSelect={handleAssetSelect}
            telematicsLoading={telematicsLoading}
            showAssetError={showAssetError}
            assetErrorRef={assetErrorRef}
          />
        );
      case 3:
        return <EventLocationStep {...commonProps} telematicsLoading={telematicsLoading} />;
      case 4:
        return (
          <EventDetailsAndCustomStep
            customFields={customFields}
            currencyOptions={currencyOptions}
            loading={loadingCustomFields}
          />
        );
      case 5:
        return <EventMediaStep formData={formData} updateFormData={updateFormData} />;
      case 6:
        return <EventSummaryStep formData={formData} lookups={lookups} customFields={customFields} />;
      default:
        return null;
    }
  };

  return (
    <div className="event-wizard-page">
      <Spin spinning={loadingInitialData || submitting} tip={submitting ? 'Creating event...' : 'Loading event form...'}>
        <Form
          form={form}
          layout="vertical"
          initialValues={INITIAL_FORM_DATA}
          preserve
          onValuesChange={(changedValues) => updateFormData(changedValues)}
        >
          <div className="event-wizard-panel">
            <Title level={2} className="event-wizard-title">
              Create Event
            </Title>
            <Steps
              current={currentStep}
              direction="horizontal"
              size="small"
              responsive={false}
              className="event-wizard-steps"
            >
              {stepItems.map((step, index) => (
                <Step key={index} icon={step.icon} />
              ))}
            </Steps>
            {renderStep()}
            <div className="event-wizard-actions">
              <Button onClick={previousStep} disabled={currentStep === 0 || submitting}>
                Previous
              </Button>
              {currentStep === stepItems.length - 1 ? (
                <Button type="primary" onClick={handleSubmit} loading={submitting} disabled={submitting}>
                  Generate This Event
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={nextStep}
                  disabled={loadingInitialData || telematicsLoading || (currentStep === 4 && loadingCustomFields)}
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default EventWizard;
